import * as React from 'react';
import { Component } from 'react';
import { History } from 'history';
import { Link } from 'react-router-dom';
import propz from 'propz';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { deleteUser } from 'Src/helpers/service/admin/user';
import { Loader } from 'Src/components/Loader/Loader';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { TerminatingForm } from './TerminatingForm/TerminatingForm';
import './SubscriptionsTab.scss';

interface Props {
  user: AppUser;
  setAppDefaultState: () => void;
  history: History;
}

interface State {
  isLoading: boolean;
  isTerminatingModalOpen: boolean;
  isConfirmationModalOpen: boolean;
  isSuccessModalOpen: boolean;
  email: string;
  password: string;
  isAgree: boolean;
  isError: boolean;
}

export class SubscriptionsTab extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isTerminatingModalOpen: false,
      isConfirmationModalOpen: false,
      isSuccessModalOpen: false,
      email: '',
      password: '',
      isAgree: false,
      isError: false
    };
  }

  renderTerminatingModal = () => {
    const { isTerminatingModalOpen, email, password, isError, isAgree } = this.state;
    return (
      <SimpleModal
        isOpen={isTerminatingModalOpen}
        title={'Terminate subscription'}
        onCloseIconClick={this.onCloseTerminatingModalClick}
        customClass="upProfileCompactModal upSubscriptionAuthModal"
      >
        <TerminatingForm
          onSubmit={this.onSubmitTerminatingModal}
          onCloseClick={this.onCloseTerminatingModalClick}
          email={email}
          password={password}
          onEmailChange={this.onEmailChange}
          onPasswordChange={this.onPasswordChange}
          isAgree={isAgree}
          onAgreementChange={this.onAgreementChange}
          isError={isError}
        />
      </SimpleModal>
    );
  };

  onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: event.target.value });
  };

  onPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ password: event.target.value });
  };

  onAgreementChange = () => {
    this.setState({ isAgree: !this.state.isAgree });
  };

  onSubmitTerminatingModal = () => {
    this.setState({
      isTerminatingModalOpen: false,
      isConfirmationModalOpen: true,
      isError: false
    });
  };

  onCloseTerminatingModalClick = () => {
    this.setState({
      isTerminatingModalOpen: false,
      email: '',
      password: '',
      isAgree: false,
      isError: false
    });
  };

  renderConfirmationModal = () => {
    const { isConfirmationModalOpen } = this.state;
    return (
      <SimpleModal
        isOpen={isConfirmationModalOpen}
        title={'Terminate subscription'}
        buttonText={'Terminate subscription'}
        buttonCancelText={'Cancel'}
        onButtonClick={this.onSubmitConfirmationModal}
        onCloseClick={this.onCloseConfirmationModalClick}
        onCloseIconClick={this.onCloseConfirmationModalClick}
        customClass="upProfileCompactModal upProfileDangerModal"
      >
        <div className="upSubscriptionConfirmText">
          <p>
            This permanently closes your Squad In Touch account. You will immediately lose access to every linked
            school, role and service.
          </p>
          <p>
            Your personal profile, sign-in sessions, roles and notification channels will be deleted or irreversibly
            anonymised in accordance with our{' '}
            <a
              className="upSubscriptionDocLink"
              href="https://www.squadintouch.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
          <p>
            Records that must be retained for legal, security or legitimate operational purposes may remain in
            anonymised form as described in the policy.
          </p>
          <p>This action cannot be undone.</p>
        </div>
      </SimpleModal>
    );
  };

  onSubmitConfirmationModal = () => {
    const { user } = this.props;
    const { email, password } = this.state;
    this.setState({ isLoading: true });

    deleteUser(user, email, password)
      .then(() => {
        this.setState({
          isConfirmationModalOpen: false,
          isSuccessModalOpen: true,
          isLoading: false,
          email: '',
          password: '',
          isAgree: false,
          isError: false
        });
      })
      .catch(() => {
        this.setState({
          isConfirmationModalOpen: false,
          isTerminatingModalOpen: true,
          isLoading: false,
          isError: true
        });
      });
  };

  onCloseConfirmationModalClick = () => {
    this.setState({
      isConfirmationModalOpen: false,
      email: '',
      password: '',
      isAgree: false,
      isError: false
    });
  };

  renderSuccessModal = () => {
    const { isSuccessModalOpen } = this.state;
    return (
      <SimpleModal
        title={'Subscription terminated'}
        isOpen={isSuccessModalOpen}
        buttonText={'Ok'}
        body={
          'Your Squad In Touch account has been closed and your personal account data has been removed or anonymised.'
        }
        onButtonClick={this.onCloseSuccessModalClick}
        onCloseIconClick={this.onCloseSuccessModalClick}
        customClass="upProfileCompactModal"
      />
    );
  };

  onCloseSuccessModalClick = () => {
    this.setState({ isSuccessModalOpen: false });
    this.props.setAppDefaultState();
    this.props.history.push('/login');
  };

  onCancelSubscriptionClick = () => {
    const email = propz.get(this.props.user, ['profile', 'email'], '');
    this.setState({
      isTerminatingModalOpen: true,
      email,
      password: '',
      isAgree: false,
      isError: false
    });
  };

  render() {
    const { isLoading } = this.state;
    const { user } = this.props;
    const createdAt = propz.get(user, ['profile', 'createdAt']);
    const createdAtConverted = createdAt ? moment(createdAt).format('D MMM YYYY HH:mm') : '-';

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="upSubscriptionsTab">
        {this.renderTerminatingModal()}
        {this.renderConfirmationModal()}
        {this.renderSuccessModal()}

        <div className="upProfileInfo">
          <span className="upProfileInfoIcon">i</span>
          <div>
            <div>View your current registered-user subscription and terms.</div>
            <div>Terminating your subscription permanently removes your Squad In Touch account and personal data.</div>
          </div>
        </div>

        <section className="upProfileCard">
          <header className="upProfileCardHeader">
            <div>
              <h2 className="upProfileCardTitle">Subscriptions</h2>
              <p className="upProfileCardSub">Your active account subscription.</p>
            </div>
          </header>
          <div className="upProfileCardBody upProfileTableCard">
            <table className="upProfileTable upSubscriptionsTable">
              <thead>
                <tr>
                  <th>Subscription</th>
                  <th>Subscribed since</th>
                  <th>Fee</th>
                  <th>T&Cs</th>
                  <th className="upSubscriptionsActionHead">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="upSubscriptionsName">Squad In Touch registered user subscription</td>
                  <td>{createdAtConverted}</td>
                  <td>Free of charge</td>
                  <td>
                    <Link className="upSubscriptionDocLink" to="/dist/images/docs/terms_of_use.pdf" target="_blank">
                      Terms of use
                    </Link>
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={this.onCancelSubscriptionClick}
                      className="upProfileActionLink upProfileActionLinkDanger"
                    >
                      Terminate
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }
}
